import { useStoreProductLots } from '@gozoki/api'
import type { StoreAvailableProduct } from '@gozoki/api-types'
import { frenchFormat } from '@gozoki/tools'
import { DateTime } from 'luxon'
import { useEffect } from 'react'
import { ModalCloseButton } from './ui/ModalCloseButton'

const unitLabel = (units: number) => `unité${units > 1 ? 's' : ''}`

/**
 * Fiche produit (Figma 10263:2257), ouverte au clic sur une carte de la modale
 * « Produits disponibles » : le stock du produit ventilé par lot.
 *
 * Empilée au-dessus de la liste (z-index supérieur) plutôt que de la remplacer : fermer la fiche
 * remet la grille et ses filtres exactement dans l'état où on les avait laissés.
 */
export function ProductLotsModal({
  product,
  onClose,
}: {
  product: StoreAvailableProduct
  onClose: () => void
}) {
  const { data, isLoading } = useStoreProductLots(product.reference)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // La liste écoute aussi Échap : sans ça, une seule frappe fermerait les deux niveaux.
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [onClose])

  const lots = data?.lots ?? []

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#072C1A40] p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label={`Détail par lot — ${product.label}`}
      // La fiche est rendue dans l'overlay de la liste, qui se ferme au clic : sans arrêter la
      // propagation, un clic à côté fermerait les deux niveaux d'un coup.
      onClick={(event) => {
        event.stopPropagation()
        onClose()
      }}
    >
      <div
        className="flex max-h-full w-[824px] max-w-full flex-col gap-[20px] overflow-y-auto kiosk-scroll-y rounded-[20px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-[12px]">
          <h2 className="min-w-0 truncate font-sans text-[22px] leading-[24px] font-semibold text-[#181D27] uppercase">
            {product.label}
          </h2>
          <ModalCloseButton onClose={onClose} ariaLabel="Fermer la fiche produit" />
        </div>

        <div className="flex items-center gap-[14px]">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt=""
              className="size-[64px] shrink-0 rounded-[12px] object-cover"
            />
          ) : (
            <span className="size-[64px] shrink-0 rounded-[12px] bg-[#EFEBDD]" />
          )}

          <span className="flex min-w-0 flex-col gap-[4px]">
            <span className="font-sans text-[14px] text-[#6B6B6B]">
              {product.categoryLabel ?? '—'}
            </span>
            <span className="font-sans text-[15px] text-[#6B6B6B]">
              <span
                className="font-bold"
                style={{ color: product.belowMinimum ? '#DC2626' : '#2A9D5C' }}
              >
                {frenchFormat(product.stock)} {unitLabel(product.stock)}
              </span>{' '}
              en magasin — détail par lot
            </span>
          </span>
        </div>

        <div className="flex flex-col gap-[10px]">
          {lots.map((entry) => (
            <div
              key={`${entry.lot}-${entry.dlc}`}
              className="flex items-center gap-[18px] rounded-[14px] border border-[#E3E1D8] bg-white px-[19px] py-[15px]"
            >
              <span className="w-[110px] shrink-0 truncate font-sans text-[14px] font-bold text-[#072C1A]">
                {entry.lot}
              </span>

              <span className="min-w-0 flex-1 font-sans text-[13px] text-[#6B6B6B]">
                Arrivé le{' '}
                <span className="font-semibold text-[#072C1A]">
                  {entry.arrivedAt
                    ? DateTime.fromISO(entry.arrivedAt)
                        .setZone('Europe/Paris')
                        .toFormat('dd/MM HH:mm')
                    : '—'}
                </span>
              </span>

              <span className="min-w-0 flex-1 font-sans text-[13px] text-[#6B6B6B]">
                DLC{' '}
                <span className="font-semibold text-[#072C1A]">
                  {DateTime.fromISO(entry.dlc).toFormat('dd/MM')}
                </span>
              </span>

              <span className="w-[90px] shrink-0 text-right font-sans text-[13px] text-[#6B6B6B]">
                <span className="font-bold text-[#072C1A]">{frenchFormat(entry.units)}</span>{' '}
                {unitLabel(entry.units)}
              </span>
            </div>
          ))}

          {lots.length === 0 ? (
            <span className="py-[24px] text-center font-sans text-[14px] text-[#8A8676]">
              {isLoading ? 'Chargement…' : 'Aucun lot identifié pour ce produit.'}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
}
